import { Injectable, signal, computed, inject } from '@angular/core'
import { Project } from '@models/project.model'
import { ChatSession } from '@models/chat.model'
import { CreateProjectRequest, ProjectApiService, UpdateProjectRequest } from './project-api.service'
import { MediaAsset } from '@models/media-asset.model'
import { map, Observable, tap } from 'rxjs'
import { ToasterService } from './toaster.service'

@Injectable({
  providedIn: 'root',
})
export class ProjectHelperService {
  private projects = signal<Project[]>([])
  private projectsExpanded = signal<boolean>(false)
  private selectedProjectId = signal<string | null>(null)

  private projectApiService = inject(ProjectApiService)
  private toasterService = inject(ToasterService)

  getProjects = computed(() => this.projects())
  getSelectedProjectId = computed(() => this.selectedProjectId())
  isProjectsExpanded = computed(() => this.projectsExpanded())

  loadProjects(): Observable<Project[]> {
    return this.projectApiService.getAllProjects().pipe(
      tap(response => {
        this.projects.set(response.projects)
        this.saveProjects()
      }),
      map(response => response.projects)
    )
  }

  saveProjects(): void {
    localStorage.setItem('media-hub-projects', JSON.stringify(this.projects()))
  }

  setSelectedProjectId(projectId: string): void {
    this.selectedProjectId.set(projectId)
  }

  createProject(name: string = 'Untitled'): Observable<Project> {
    var createProjectRequest: CreateProjectRequest = {
      name: name,
      description: '',
      mediaAssetIds: [],
      tags: [],
      thumbnailUrl: '',
    }

    return this.projectApiService.createProject(createProjectRequest).pipe(
      tap(response => {
        if (response.success) {
          this.projects.update(projects => [...projects, response.project])
          this.saveProjects()
          if (response.message) {
            this.toasterService.success(response.message)
          }
        }
      }),
      map(response => response.project)
    )
  }

  addAssetToProject(projectId: string, asset: MediaAsset): void {
    const isMediaLibraryAsset = !!asset._mediaLibraryAssetId
    const assetIds = isMediaLibraryAsset ? [] : [asset.id]
    const mediaLibraryAssetIds = isMediaLibraryAsset ? [asset._mediaLibraryAssetId!] : undefined

    this.projectApiService.addAssetsToProject(projectId, assetIds, mediaLibraryAssetIds).subscribe({
      next: response => {
        if (response.success) {
          this.projects.update(projects =>
            projects.map(p => {
              if (p.id === projectId) {
                if (!p.mediaAssetIds.find(a => a === asset.id)) {
                  return {
                    ...p,
                    mediaAssetIds: [...p.mediaAssetIds, asset.id],
                  }
                }
              }
              return p
            })
          )
          if (response.message) {
            this.toasterService.success(response.message)
          }
        }
        this.saveProjects()
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to add asset to project')
      }
    })
  }

  removeAssetFromProject(projectId: string, assetId: string): void {
    this.projectApiService.removeAssetsFromProject(projectId, [assetId]).subscribe({
      next: response => {
        if (response.success) {
          this.projects.update(projects =>
            projects.map(p => {
              if (p.id === projectId) {
                const updatedAssets = p.mediaAssetIds.filter(a => a !== assetId)

                return {
                  ...p,
                  mediaAssetIds: updatedAssets,
                  updatedAt: new Date(),
                }
              }
              return p
            })
          )
          if (response.message) {
            this.toasterService.success(response.message)
          }
          this.saveProjects()
        }
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to remove asset from project')
      }
    })
  }

  addChatToProject(projectId: string, chat: ChatSession): void {
    if (chat.mediaAssetId) {
      this.projectApiService.addMediaAssetChatsToProject(projectId, [chat.id]).subscribe({
        next: response => {
          if (response.success) {
            this.projects.update(projects =>
              projects.map(p => {
                if (p.id === projectId) {
                  if (!p.mediaAssetChatIds.find(c => c === chat.id)) {
                    return {
                      ...p,
                      mediaAssetChatIds: [...p.mediaAssetChatIds, chat.id],
                      updatedAt: new Date(),
                    }
                  }
                }
                return p
              })
            )
            if (response.message) {
              this.toasterService.success(response.message)
            }
          }
          this.saveProjects()
        },
        error: error => {
          this.toasterService.error(error?.error?.message || 'Failed to add chat to project')
        }
      })
    } else {
      this.projectApiService.addGeneralChatsToProject(projectId, [chat.id]).subscribe({
        next: response => {
          if (response.success) {
            this.projects.update(projects =>
              projects.map(p => {
                if (p.id === projectId) {
                  if (!p.generalChatIds.find(c => c === chat.id)) {
                    return {
                      ...p,
                      generalChatIds: [...p.generalChatIds, chat.id],
                      updatedAt: new Date(),
                    }
                  }
                }
                return p
              })
            )
            if (response.message) {
              this.toasterService.success(response.message)
            }
          }
          this.saveProjects()
        },
        error: error => {
          this.toasterService.error(error?.error?.message || 'Failed to add chat to project')
        }
      })
    }
  }

  removeChatFromProject(projectId: string, chat: ChatSession): void {
    if (chat.mediaAssetId) {
      this.projectApiService.removeMediaAssetChatsFromProject(projectId, [chat.id]).subscribe({
        next: response => {
          if (response.success) {
            this.projects.update(projects =>
              projects.map(p => {
                if (p.id === projectId) {
                  return {
                    ...p,
                    mediaAssetChatIds: p.mediaAssetChatIds.filter(c => c !== chat.id),
                    updatedAt: new Date(),
                  }
                }
                return p
              })
            )
            if (response.message) {
              this.toasterService.success(response.message)
            }
            this.saveProjects()
          }
        },
        error: error => {
          this.toasterService.error(error?.error?.message || 'Failed to remove chat from project')
        }
      })
    } else {
      this.projectApiService.removeGeneralChatsFromProject(projectId, [chat.id]).subscribe({
        next: response => {
          if (response.success) {
            this.projects.update(projects =>
              projects.map(p => {
                if (p.id === projectId) {
                  return {
                    ...p,
                    generalChatIds: p.generalChatIds.filter(c => c !== chat.id),
                    updatedAt: new Date(),
                  }
                }
                return p
              })
            )
            if (response.message) {
              this.toasterService.success(response.message)
            }
            this.saveProjects()
          }
        },
        error: error => {
          this.toasterService.error(error?.error?.message || 'Failed to remove chat from project')
        }
      })
    }
  }

  addPlaylistToProject(projectId: string, playlistId: string): void {
    this.projectApiService.addPlaylistsToProject(projectId, [playlistId]).subscribe({
      next: response => {
        if (response.success) {
          this.projects.update(projects =>
            projects.map(p => {
              if (p.id === projectId) {
                if (!p.playlistIds.find(id => id === playlistId)) {
                  return {
                    ...p,
                    playlistIds: [...p.playlistIds, playlistId],
                    updatedAt: new Date(),
                  }
                }
              }
              return p
            })
          )
          if (response.message) {
            this.toasterService.success(response.message)
          }
          this.saveProjects()
        }
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to add playlist to project')
      }
    })
  }

  removePlaylistFromProject(projectId: string, playlistId: string): void {
    this.projectApiService.removePlaylistsFromProject(projectId, [playlistId]).subscribe({
      next: response => {
        if (response.success) {
          this.projects.update(projects =>
            projects.map(p => {
              if (p.id === projectId) {
                return {
                  ...p,
                  playlistIds: p.playlistIds.filter(id => id !== playlistId),
                  updatedAt: new Date(),
                }
              }
              return p
            })
          )
          if (response.message) {
            this.toasterService.success(response.message)
          }
          this.saveProjects()
        }
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to remove playlist from project')
      }
    })
  }

  deleteProject(projectId: string): void {
    this.projectApiService.deleteProject(projectId).subscribe({
      next: response => {
        if (response.success) {
          if (response.message) {
            this.toasterService.success(response.message)
          }
          this.projects.update(projects => projects.filter(p => p.id !== projectId))
          this.saveProjects()
        }
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to remove project')
      }
    })
  }

  renameProject(projectId: string, newName: string): void {
    const updateProjectRequest: UpdateProjectRequest = { name: newName }

    this.projectApiService.updateProject(projectId, updateProjectRequest).subscribe({
      next: response => {
        if (response.success) {
          this.projects.update(projects => projects.map(p => (p.id === projectId ? { ...p, name: newName } : p)))
          if (response.message) {
            this.toasterService.success(response.message)
          } else {
            this.toasterService.success('Project renamed successfully')
          }
        }
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to rename project')
      }
    })
  }

  getProjectFromList(projectId: string): Project | undefined {
    return this.projects().find(p => p.id === projectId)
  }

  getProject(projectId: string): Observable<Project> {
    return this.projectApiService.getProjectById(projectId).pipe(
      tap(response => {
        if (response.success && response.project) {
          const existingProjectIndex = this.projects().findIndex(p => p.id === projectId)

          if (existingProjectIndex !== -1) {
            this.projects.update(projects =>
              projects.map((p, i) => i === existingProjectIndex ? response.project : p)
            )
          } else {
            this.projects.update(projects => [...projects, response.project])
          }
          this.saveProjects()
        }
      }),
      map(response => response.project)
    )
  }

  expandProjects(): void {
    this.projectsExpanded.set(true)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('expand-projects-section'))
    }
  }

  collapseProjects(): void {
    this.projectsExpanded.set(false)
  }

  toggleProjectsExpanded(): void {
    this.projectsExpanded.update(expanded => !expanded)
  }
}
